import withPaginationV2 from '../../../../hoc/pagination/withPaginationV2';
import AdmicityTableV2 from '../../../../shared-components/Table/V2/AdmicityTableV2';
import {
    useDeleteEnquirySubmissionsMutation,
    useGetEnquiryResponsesQuery,
    useGetNonExistingSchoolsQuery
} from '../../../../api/services/formsService';
import React, {useEffect} from 'react';
import {
    Alert,
    Box
} from '@mui/material';
import CheckBoxRoundedIcon from '@mui/icons-material/CheckBoxRounded';
import FILTER_OPERATIONS from '../../../../constants/filterOperations';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import useFileDownloader from '../../../../utility/hooks/useFileDownloader';
import useTableState from '../../../../utility/hooks/useTableState';
import {downloadEnquiryImportFile} from '../../../../api/services/filesService';
import {formatDate} from '../../../../utility/dateUtil';
import DateRangePicker from '../../../../shared-components/Table/V2/components/columnFilters/DateRangePicker';
import ContentPasteOffRoundedIcon from '@mui/icons-material/ContentPasteOffRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import {useConfirm} from 'material-ui-confirm';
import useNotification from '../../../../utility/hooks/useNotification';
import {useGetContactTitlesQuery, useGetRelationsQuery} from '../../../../api/services/lookupsService';
import {useGetYearGroupsQuery} from '../../../../api/services/groupService';

const getYearsOptions = () => {
    const currentYear = new Date().getFullYear();

    return Array.from({length: 8}, (_, i) => {
        const year = currentYear - 1 + i;

        return ({title: `${year}`, value: year});
    });
};

const tableKey = 'EnquirySubmissionsTable';

const Table = withPaginationV2(AdmicityTableV2, {func: useGetEnquiryResponsesQuery});

const NonExistingSchoolsAlert = () => {
    const { data: nonExistingSchools = [], isLoading } = useGetNonExistingSchoolsQuery();

    if (isLoading || nonExistingSchools.length === 0) {
        return null;
    }

    return (
        <Box mb={2}>
            <Alert severity="info">
                {nonExistingSchools.length} school{nonExistingSchools.length !== 1 ? 's' : ''} from enquiries {nonExistingSchools.length !== 1 ? 'are' : 'is'} not added in the SIMS: {nonExistingSchools.join(', ')}
            </Alert>
        </Box>
    );
};

const EnquirySubmissions = () => {
    const confirm = useConfirm();
    const downloadFile = useFileDownloader();
    const {filters} = useTableState(tableKey);
    const [deleteSubmissions, {
        isError: isDeleteEnquirySubmissionsError
    }] = useDeleteEnquirySubmissionsMutation();
    const {showErrorNotification} = useNotification();
    const {data: titles = []} = useGetContactTitlesQuery();
    const {data: yearGroups = []} = useGetYearGroupsQuery();
    const {data: relations = []} = useGetRelationsQuery();

    useEffect(() => {
        if (isDeleteEnquirySubmissionsError) {
            showErrorNotification('Failed to delete form submissions');
        }
    }, [isDeleteEnquirySubmissionsError]);

    /* eslint-disable react/prop-types */
    const columns = [
        {
            accessorKey: 'enquirerTitle',
            header: 'Title',
            size: 80,
            filterVariant: 'select',
            filterFn: FILTER_OPERATIONS.EQUALS,
            filterSelectOptions: titles?.map(title => ({
                label: title.description,
                value: title.id,
            })),
        },
        {
            accessorKey: 'enquirerForename',
            header: 'Forename'
        },
        {
            accessorKey: 'enquirerSurname',
            header: 'Surname',
        },
        {
            accessorKey: 'enquirerEmail',
            header: 'Email',
        },
        {
            accessorKey: 'enquirerRelationship',
            header: 'Relationship',
            size: 80,
            filterVariant: 'select',
            filterFn: FILTER_OPERATIONS.EQUALS,
            filterSelectOptions: relations?.map(relation => ({
                label: relation.description,
                value: relation.description,
            })),
        },
        {
            accessorKey: 'enquirerMobile',
            header: 'Mobile Phone',
        },
        {
            accessorKey: 'pupilForename',
            header: 'Forename',
        },
        {
            accessorKey: 'pupilSurname',
            header: 'Surname',
        },
        {
            accessorKey: 'pupilGender',
            header: 'Gender',
            filterVariant: 'select',
            filterFn: FILTER_OPERATIONS.EQUALS,
            filterSelectOptions: [
                {label: 'Male', value: 'male'},
                {label: 'Female', value: 'female'}
            ],
        },
        {
            accessorKey: 'pupilDateOfBirth',
            header: 'Date of Birth',
            Cell: ({cell}) => formatDate(cell.getValue()),
            Filter: DateRangePicker,
            filterFn: FILTER_OPERATIONS.BETWEEN_INCLUSIVE
        },
        {
            accessorKey: 'pupilCurrentSchool',
            header: 'Current School',
        },
        {
            accessorKey: 'pupilYearEntry',
            header: 'Proposed Year of Entry',
            filterVariant: 'multi-select',
            filterFn: FILTER_OPERATIONS.MULTISELECT,
            filterSelectOptions: getYearsOptions(),
        },
        {
            accessorKey: 'pupilSeasonEntry',
            header: 'Proposed Season of Entry',
            filterVariant: 'multi-select',
            filterFn: FILTER_OPERATIONS.MULTISELECT,
            filterSelectOptions: [
                {label: 'Autumn', value: 'Autumn'},
                {label: 'Spring', value: 'Spring'},
                {label: 'Summer', value: 'Summer'}
            ],
        },
        {
            accessorKey: 'pupilYearGroup',
            header: 'Proposed Year Group',
            filterVariant: 'multi-select',
            filterFn: FILTER_OPERATIONS.MULTISELECT,
            filterSelectOptions: yearGroups?.map(g => ({
                label: g.name,
                value: g.id,
            })),
        },
        {
            accessorKey: 'preferredVisitDate',
            header: 'Preferred Visit Date',
            Cell: ({cell}) => cell.getValue() && formatDate(cell.getValue()),
            Filter: DateRangePicker,
            filterFn: FILTER_OPERATIONS.BETWEEN_INCLUSIVE
        },
        {
            accessorKey: 'marketingSource',
            header: 'Marketing Source',
            filterVariant: 'multi-select',
            filterFn: FILTER_OPERATIONS.MULTISELECT,
            filterSelectOptions: [
                {label: 'Internet', value: 'Internet'},
                {label: 'Word of Mouth', value: 'Word of Mouth'},
                {label: 'Event', value: 'Event'},
                {label: 'Other', value: 'Other'},
            ],
        },
        {
            accessorKey: 'optInUpdates',
            header: 'Opt-in for updates',
            filterVariant: 'select',
            filterFn: FILTER_OPERATIONS.EQUALS,
            filterSelectOptions: [
                { value: true, label: 'Selected' },
                { value: false, label: 'Not Selected' }
            ],
            Cell: ({ cell }) => cell.getValue() === true
                ? <CheckBoxRoundedIcon fontSize="small" color="primary" />
                : '',
        },
        {
            accessorKey: 'submittedAt',
            header: 'Submitted At',
            Cell: ({cell}) => formatDate(cell.getValue()),
            Filter: DateRangePicker,
            filterFn: FILTER_OPERATIONS.BETWEEN_INCLUSIVE,
        },
    ];
    /* eslint-enable react/prop-types */

    const generateImportFile = async () =>
        await downloadFile(downloadEnquiryImportFile, {filters: filters.filters || []});

    const generateImportFileByResponseIds = async (rows) => {
        await downloadFile(downloadEnquiryImportFile, {responseIds: rows.map(x => x.id)});
    };

    const handleEnquiriesDelete = async rows => {
        const description = rows.length === 1
            ? 'Are you sure you want to delete this enquiry form submission?'
            : `Are you sure you want to delete the selected (${rows.length}) enquiry form submissions?`;

        return confirm({
            title: 'Confirm Deletion',
            description,
            confirmationText: 'Delete'
        })
            .then(async () => {
                await deleteSubmissions({ids: rows.map(x => x.id)});
            })
            .catch(_ => {
            });
    };

    return (
        <>
            <Box sx={{mt: 2}} />
            <NonExistingSchoolsAlert />
            <Table
                id={tableKey}
                columns={columns}
                tableProps={{
                    enableSorting: false,
                    enableColumnResizing: false,
                    enableRowActions: true,
                    enableRowSelection: true,
                    muiFilterTextFieldProps: {
                        sx: {
                            minWidth: 180
                        }
                    },
                }}
                toolbarActions={
                    [{
                        icon: <FileDownloadRoundedIcon/>,
                        type: 'buttonSelect',
                        label: 'Generate import file',
                        disableWhenEmpty: true,
                        options: [
                            {
                                label: 'For all records',
                                onClick: generateImportFile
                            },
                            {
                                label: 'For selected records',
                                onClick: generateImportFileByResponseIds,
                                enableOnRowSelect: true
                            },
                        ]
                    },
                        {
                            type: 'button',
                            label: 'Delete',
                            size: 'small',
                            renderOnRowSelect: true,
                            startIcon: <DeleteOutlineRoundedIcon/>,
                            onClick: handleEnquiriesDelete
                        }]
                }
                noRowsOverlay={{
                    icon: <ContentPasteOffRoundedIcon/>,
                    text: 'No responses yet'
                }}
            >
            </Table>
        </>
    );
};

export default EnquirySubmissions;